import * as React from "react";
import {useEffect} from "react";
import ReactTable from "react-table";
import {AiScriptChangeIteration} from "../../../../../generated/api";
import {Button, Form, Header, Icon, Input, Message, Modal, Segment} from "semantic-ui-react";
import {AiAgentTaskRawModal} from "./AiAgentTaskRawModal";
import {aiAgentTasks} from "./AiScripterPage";
import {signal} from "@preact/signals-react";
import {backend} from "../../../../../xconvert-backend";
import {authentication} from "../../../../../authentication";
import Notifications, {notify} from 'react-notify-toast';

export interface AiAgentTaskTableProps {
    loadAgentTask: (agentTask: any) => void;
    loadedAgentTask: any;
    onViewTransportOrders: (orderIds: string[]) => void;
    onViewQueuedEvents?: (eventIds: string[]) => void; // Add this line
}

export const aiAgentTaskTableFilters = signal({
    searchId: "",
    filterAiModel: "",
    filterRequirements: "",
    filterFromDate: "",
    filterToDate: "",
    filterStatus: "",
    showFilters: false,
    sorted: [{id: 'createdAt', desc: true}],
});

export function AiAgentTaskTable(props: React.PropsWithChildren<AiAgentTaskTableProps>) {

    const [filteredAgents, setFilteredAgents] = React.useState([]);
    const [openInRawModal, setOpenInRawModal] = React.useState(null);
    const [requirementsModal, setRequirementsModal] = React.useState({
        open: false,
        content: "",
    });
    const [cancellingTasks, setCancellingTasks] = React.useState<Set<string>>(new Set());
    const currentFilters = aiAgentTaskTableFilters.value;

    useEffect(() => {
        let newFilteredAgents = aiAgentTasks.value || [];

        if (currentFilters.searchId) {
            newFilteredAgents = newFilteredAgents.filter((a) =>
                a._id?.toLowerCase().includes(currentFilters.searchId.toLowerCase())
            );
        }

        if (currentFilters.filterAiModel) {
            newFilteredAgents = newFilteredAgents.filter((a) =>
                a.aiModelEnum?.toLowerCase().includes(currentFilters.filterAiModel.toLowerCase())
            );
        }

        if (currentFilters.filterRequirements) {
            newFilteredAgents = newFilteredAgents.filter((a) =>
                a.requirements?.toLowerCase().includes(currentFilters.filterRequirements.toLowerCase())
            );
        }

        if (currentFilters.filterFromDate) {
            const fromDate = new Date(currentFilters.filterFromDate);
            fromDate.setHours(0, 0, 0, 0);
            newFilteredAgents = newFilteredAgents.filter((a) => {
                const createdAt = new Date(a.createdAt);
                return createdAt >= fromDate;
            });
        }
        if (currentFilters.filterToDate) {
            const toDate = new Date(currentFilters.filterToDate);
            toDate.setHours(23, 59, 59, 999);
            newFilteredAgents = newFilteredAgents.filter((a) => {
                const createdAt = new Date(a.createdAt);
                return createdAt <= toDate;
            });
        }

        if (currentFilters.filterStatus) {
            newFilteredAgents = newFilteredAgents.filter((a) => {
                const status = getAiAgentTaskStatus(a);
                return status === currentFilters.filterStatus;
            });
        }

        setFilteredAgents(newFilteredAgents);
    }, [
        currentFilters.searchId,
        aiAgentTasks.value,
        currentFilters.filterAiModel,
        currentFilters.filterRequirements,
        currentFilters.filterFromDate,
        currentFilters.filterToDate,
        currentFilters.filterStatus,
    ]);

    const updateFilter = (key: keyof typeof currentFilters, value: any) => {
        aiAgentTaskTableFilters.value = {
            ...aiAgentTaskTableFilters.value,
            [key]: value,
        };
    };

    const cancelTask = async (task: any) => {
        try {
            setCancellingTasks(prev => new Set(prev).add(task._id));

            const updatedTask = {
                ...task,
                cancelledByUser: true
            };

            const auth = backend.withTokenAuthHeader(authentication.token);
            await backend.internalApi.createOrModifyAiAgentTask(updatedTask, auth);


            notify.show('Task cancelled successfully', 'success', 3000);

        } catch (error) {
            console.error('Failed to cancel task:', error);
            notify.show('Failed to cancel task', 'error', 5000);
        } finally {
            setCancellingTasks(prev => {
                const newSet = new Set(prev);
                newSet.delete(task._id);
                return newSet;
            });
        }
    };


    const columns = [
        {
            Header: "Cancel",
            id: 'cancel-action',
            accessor: row => row._id,
            Cell: (row) => {
                const status = getAiAgentTaskStatus(row.original);
                const canCancel = status !== "DONE" && row.original.cancelledByUser !== true;
                const isCancelling = cancellingTasks.has(row.original._id);

                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        {canCancel ? (
                            <Button
                                size="medium"
                                basic
                                icon
                                onClick={() => cancelTask(row.original)}
                                loading={isCancelling}
                                disabled={isCancelling}
                                title="Cancel Task"
                                style={{
                                    padding: '0.2em',
                                    margin: 0,
                                    border: 'none',
                                    boxShadow: 'none',
                                }}
                            >
                                <Icon name="close" color="black" />
                            </Button>
                        ) : (
                            <Icon  name="check circle" color="black" title="Done or Canceled" />
                        )}
                    </div>
                );
            },

            width: 55,
            minWidth: 55,
            maxWidth: 55,
            disableSortBy: true,
            disableFilters: true,
        },
        {Header: "ID", accessor: "_id"},
        {
            Header: "Load",
            Cell: (row) => (
                <Button
                    size="tiny"
                    color="blue"
                    icon
                    labelPosition="left"
                    onClick={() => props.loadAgentTask(row.original)}
                >
                    <Icon name="download"/> Load
                </Button>
            ),
        },
        {
            Header: "Raw",
            Cell: (row) => (
                <Button
                    size="tiny"
                    color="black"
                    icon
                    labelPosition="left"
                    onClick={() => setOpenInRawModal(row.original)}
                >
                    <Icon name="code"/> Raw
                </Button>
            ),
        },
        // Original Cancel column is now removed from here
        {
            Header: "Status",
            accessor: "iterations",
            Cell: (row) => <div>{getAiAgentTaskStatus(row.original)}</div>,
        },
        {
            Header: "AI Model",
            accessor: "aiModelEnum",
            Cell: (row) => <div>{row.original.aiModelEnum}</div>,
        },
        {
            Header: "Requirements",
            accessor: "requirements",
            Cell: (row) => {
                const content = row.original.requirements || "";
                return (
                    <div
                        onClick={() => setRequirementsModal({
                            open: true,
                            content: content
                        })}
                        style={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "150px"
                        }}
                        title="Click to view full content"
                    >
                        {content}
                    </div>
                );
            },
        },
        {
            Header: "IDs",
            id: 'example-ids',
            Cell: (row) => {
                const transportOrderIds = row.original.exampleTransportOrderIds || [];
                const queuedEventIds = row.original.exampleQueuedEventIds || [];

                const hasTransportOrders = transportOrderIds.length > 0;
                const hasQueuedEvents = queuedEventIds.length > 0;

                if (!hasTransportOrders && !hasQueuedEvents) {
                    return <div>None</div>;
                }

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {hasTransportOrders && (
                            <Button
                                size="tiny"
                                color="blue"
                                onClick={() => props.onViewTransportOrders(transportOrderIds)}
                                style={{ margin: '1px 0' }}
                            >
                                TO ({transportOrderIds.length})
                            </Button>
                        )}
                        {hasQueuedEvents && props.onViewQueuedEvents && (
                            <Button
                                size="tiny"
                                color="teal"
                                onClick={() => props.onViewQueuedEvents(queuedEventIds)}
                                style={{ margin: '1px 0' }}
                            >
                                QE ({queuedEventIds.length})
                            </Button>
                        )}
                    </div>
                );
            },
        },
        {
            Header: "Created At",
            accessor: "createdAt",
            Cell: (row) => (
                <div>{new Date(row.original.createdAt).toLocaleString()}</div>
            ),
        },
    ];

    return (
        <Segment raised style={{padding: "1rem", margin: "2px"}}>
            <Header as="h2" style={{marginBottom: "1rem", display: 'flex', alignItems: 'center'}}>
                <Icon name="tasks"/>
                <Header.Content>AI Agent Tasks</Header.Content>
                <Button
                    icon
                    labelPosition='right'
                    onClick={() => updateFilter('showFilters', !currentFilters.showFilters)}
                    style={{
                        marginLeft: 'auto',
                        padding: '0.4em 0.4em',
                        fontSize: '0.6em',
                        minHeight: 'auto',
                        minWidth: 'auto',
                    }}
                >
                    {currentFilters.showFilters ? 'Hide Filters' : 'Show Filters'}
                    <Icon name={currentFilters.showFilters ? 'angle up' : 'angle down'} />
                </Button>
            </Header>
            {currentFilters.showFilters && (
                <Segment style={{ marginBottom: "0.8rem" }}>
                    <Header as="h4" dividing>Filters</Header>
                    <Form>
                        <Form.Group widths="equal">
                            <Form.Select
                                label="Status"
                                placeholder="Filter by Status..."
                                options={[
                                    { key: 'all', text: 'All Statuses', value: '' },
                                    ...Object.values(AiScriptChangeIteration.StatusEnum).map(s => ({ key: s, text: s, value: s }))
                                ]}
                                value={currentFilters.filterStatus}
                                onChange={(e, { value }) => updateFilter('filterStatus', value as string)}
                                clearable
                            />

                            <Form.Input
                                label="Requirements (contains)"
                                placeholder="Filter by Requirements text..."
                                value={currentFilters.filterRequirements}
                                onChange={(e, { value }) => updateFilter('filterRequirements', value)}
                                clearable
                            />
                        </Form.Group>
                        <Form.Group widths="equal" stackable>
                            <Form.Input
                                label="AI Model"
                                placeholder="Filter by AI Model"
                                value={currentFilters.filterAiModel}
                                onChange={(e, { value }) => updateFilter('filterAiModel', value)}
                                clearable
                            />
                            <Form.Input
                                type="date"
                                label="Created From"
                                value={currentFilters.filterFromDate}
                                onChange={(e, { value }) => updateFilter('filterFromDate', value)}
                                clearable
                            />
                            <Form.Input
                                type="date"
                                label="Created To"
                                value={currentFilters.filterToDate}
                                onChange={(e, { value }) => updateFilter('filterToDate', value)}
                                clearable
                            />
                        </Form.Group>
                    </Form>
                </Segment>
            )}
            <Input
                icon="search"
                placeholder="Search by ID..."
                value={currentFilters.searchId}
                onChange={(e) => updateFilter('searchId', e.target.value)}
                style={{marginBottom: "0.8rem", width: "100%"}}
            />
            <div
                style={{height: "calc(100vh - 200px)", overflowY: "auto", overflowX: "auto", marginBottom: "5px"}}
            >
                <ReactTable
                    data={filteredAgents}
                    pageSize={filteredAgents.length > 0 ? filteredAgents.length : 1}
                    showPagination={false}
                    columns={columns}
                    className="-striped -highlight"
                    sorted={currentFilters.sorted}
                    onSortedChange={newSorted => updateFilter('sorted', newSorted)}
                    getTrProps={(state, rowInfo) => {
                        const agent = rowInfo?.original;

                        if (!agent || typeof agent._id === 'undefined') {
                            return { style: {} };
                        }

                        const status = getAiAgentTaskStatus(agent);
                        const rowColor = aiAgentTaskStatusRowColor(status);
                        let textColor = "white"; // Default for dark backgrounds
                        if (rowColor) {
                            const lightBackgrounds = ["#f2711c", "#e6cbcb"];
                            if (lightBackgrounds.includes(rowColor.toLowerCase())) {
                                textColor = "black";
                            }
                        }

                        const isCurrentlyLoadedTask =
                            props.loadedAgentTask &&
                            typeof props.loadedAgentTask._id !== 'undefined' &&
                            agent._id === props.loadedAgentTask._id;

                        const border = isCurrentlyLoadedTask ? "3px solid #FF0000" : "";

                        return {
                            style: {
                                background: rowColor ?? "white",
                                color: textColor,
                                border: border,
                                whiteSpace: "nowrap",
                            },
                        };
                    }}
                />
            </div>
            {openInRawModal && (
                <AiAgentTaskRawModal
                    isOpen={true}
                    onClose={() => setOpenInRawModal(null)}
                    aiAgentTask={openInRawModal}
                />
            )}

            <Modal
                open={requirementsModal.open}
                onClose={() => setRequirementsModal({ open: false, content: "" })}
            >
                <Modal.Header>
                    Requirements
                    <Button
                        icon='copy'
                        size='mini'
                        floated='right'
                        onClick={() => navigator.clipboard.writeText(requirementsModal.content)}
                        style={{ marginTop: '-8px' }}
                        title="Copy to clipboard"
                        content={"copy"}
                    />
                </Modal.Header>

                <Modal.Content>
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {requirementsModal.content}
                    </div>
                </Modal.Content>

                <Modal.Actions>
                    <Button color='black' onClick={() => setRequirementsModal({ open: false, content: "" })}>
                        Close
                    </Button>
                </Modal.Actions>
            </Modal>

        </Segment>
    );
}



export function getAiAgentTaskStatus(task) {
    // Check the cancellation flag first
    if (task.cancelledByUser === true) {
        return "CANCELLED_BY_USER";
    }

    const iterations = task.iterations;
    if (!iterations || iterations.length === 0) {
        return null;
    }

    const lastIteration = iterations[iterations.length - 1];
    return lastIteration?.status ?? "UNKNOWN";
}


export function aiAgentTaskStatusRowColor(status) {
    switch (status) {
        case "DONE":
            return "#21ba45"; // Semantic green
        case "ABORTED":
            return "#db2828"; // Semantic red
        case "CANCELLED_BY_USER":
            return "#767676"; // Semantic grey for cancelled
        case "USER_INPUT_REQUIRED":
            return "#f2711c"; // Semantic orange
        case "RUNNING":
            return "#036f88"; // Semantic teal
        case "UNKNOWN":
            return "#e6cbcb"; // A light color
        default:
            return "#e8b4fb"; // A light color
    }
}

